'use client';
import Header from '@src/v3/components/header';
import Loader from '@src/v3/components/loader';
import { loaderTypes } from '@src/v3/constants/loaderTypes';
import useLoader from '@src/v3/hooks/useLoader';
import useTheme from '@src/v3/hooks/useTheme';
import { getUserType } from '@src/v3/utils/localStorage';
import MobileNav from '@src/v3/components/mobileNav';
import { Suspense, useEffect, useMemo } from 'react';
import { usePathname } from 'next/navigation';
import { APP_ROUTE } from '@src/v3/constants/routes';
import { useNavbar } from '@src/v3/contexts/NavbarContext';

const NON_STICKY_HEADER_ROUTES: string[] = ['/property/'];

// Routes that should hide header and mobile nav completely
const HIDDEN_HEADER_ROUTES = [
  APP_ROUTE.VERIFY_EMAIL,
  APP_ROUTE.RENTER_MY_APPLICATION,
  APP_ROUTE.SUBSCRIPTION,
  APP_ROUTE.VERIFICATION_REQUIRED,
  APP_ROUTE.QUICK_CREDIT,
  APP_ROUTE.INVITE_RENTER,
  APP_ROUTE.TERMS,
  APP_ROUTE.TU_AGENT_TERMS,
  APP_ROUTE.TU_RENTER_TERMS,
  APP_ROUTE.PRIVACY,
];

//Routes that should hide mobile nav completely
const HIDDEN_MOBILE_NAV_ROUTES = [
  APP_ROUTE.GENERAL_APPLICATION_APPLY_PREFIX,
  APP_ROUTE.VERIFY_EMAIL,
  APP_ROUTE.VERIFICATION_REQUIRED,
  APP_ROUTE.PUBLIC_PROPERTY,
  APP_ROUTE.RENTER_MY_APPLICATION,
  APP_ROUTE.SUBSCRIPTION,
  APP_ROUTE.QUICK_CREDIT,
  APP_ROUTE.PROFILE,
  APP_ROUTE.EDIT_PROFILE_SETTINGS,
  APP_ROUTE.GENERAL_SETTINGS,
  APP_ROUTE.SUBSCRIPTION_SETTINGS,
  APP_ROUTE.AI_MARKETING_SETTINGS,
  APP_ROUTE.INVITE_RENTER,
  APP_ROUTE.TERMS,
  APP_ROUTE.TU_AGENT_TERMS,
  APP_ROUTE.TU_RENTER_TERMS,
  APP_ROUTE.PRIVACY,
];

export default function MainLayout(props: { children: React.ReactNode }) {
  const { children } = props;
  const pathname = usePathname();
  const { isNavbarVisible } = useNavbar();

  const { isLoading, loaderType } = useLoader();
  const { setTheme, theme } = useTheme();

  // Check if current route should have non-sticky header (memoized for performance)
  // Exception: property/add and property/[id]/edit should have sticky header
  const isHeaderSticky = useMemo(() => {
    if (pathname.includes(APP_ROUTE.AGENT_ADD_NEW_PROPERTY)) return true;
    if (/\/property\/[^/]+\/edit$/.test(pathname)) return true;
    return !NON_STICKY_HEADER_ROUTES.some((route) => pathname.includes(route));
  }, [pathname]);

  // Check if current route should hide header and mobile nav completely
  const shouldHideHeader = useMemo(() => {
    return HIDDEN_HEADER_ROUTES.some((route) => pathname.includes(route));
  }, [pathname]);

  // Check if current route should hide header and mobile nav completely
  const shouldHideMobileNav = useMemo(() => {
    return HIDDEN_MOBILE_NAV_ROUTES.some((route) => pathname.includes(route));
  }, [pathname]);

  // Check if current route is RENTER_MY_APPLICATION to add overflow-hidden
  const isRenterMyApplication = useMemo(() => {
    return pathname.includes(APP_ROUTE.RENTER_MY_APPLICATION);
  }, [pathname]);

  // Check if current route is AGENT_MY_APPLICATION to add overflow-hidden
  const isAgentMyApplication = useMemo(() => {
    return (
      pathname.includes('/property/') && pathname.includes('/my-application')
    );
  }, [pathname]);

  const isChatRoute = useMemo(() => {
    return (
      pathname.includes(APP_ROUTE.AGENT_MESSAGES) ||
      pathname.includes(APP_ROUTE.RENTER_MESSAGES)
    );
  }, [pathname]);

  // Check if current route is property/add to add max-sm:overflow-hidden to body
  const isPropertyAddRoute = useMemo(() => {
    return pathname.includes(APP_ROUTE.AGENT_ADD_NEW_PROPERTY);
  }, [pathname]);

  // Explore carries the TextZeme promo bar inside <header> above the nav, so
  // its fixed header is TEXT_ZEME_BANNER_HEIGHT taller than every other route's
  // and main has to start that much lower or the bar covers the page's own top.
  const isExploreRoute = useMemo(() => {
    return pathname === APP_ROUTE.EXPLORE;
  }, [pathname]);

  // Check if current route is CRM dashboard route
  const isCrmRoute = useMemo(() => {
    return (
      pathname.includes(APP_ROUTE.AGENT_DASHBOARD_CRM) ||
      pathname.includes(APP_ROUTE.AGENT_DASHBOARD_CRM_MARKETING) ||
      pathname.includes(APP_ROUTE.AGENT_DASHBOARD_CRM_QUICK_CREDIT)
    );
  }, [pathname]);

  useEffect(() => {
    setTheme(getUserType());
  }, [setTheme]);

  // Prevent body scroll on property/add route when keyboard opens
  useEffect(() => {
    if (!isPropertyAddRoute) {
      return undefined;
    }

    const isMobile = window.innerWidth <= 640;
    if (!isMobile) {
      return undefined;
    }

    // Store original scroll position
    // eslint-disable-next-line prefer-destructuring
    const scrollY = window.scrollY;

    // Prevent scroll by setting position fixed on body only (not html)
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.left = '0';
    document.body.style.width = '100%';
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    // Avoid setting properties on html/documentElement to prevent Safari scroll issues

    // Prevent scrollIntoView from scrolling the body
    const originalScrollIntoView = Element.prototype.scrollIntoView;
    Element.prototype.scrollIntoView = function (
      options?: boolean | ScrollIntoViewOptions
    ) {
      // Only allow scrollIntoView if the element is inside a scrollable container
      const scrollableParent = this.closest('[class*="overflow-y-auto"]');
      if (scrollableParent) {
        // Allow scroll within the scrollable container
        originalScrollIntoView.call(this, options);
        return;
      }
      // Prevent body scroll - no return needed
    };

    // Prevent any scroll events on window
    const preventScroll = (e: Event) => {
      e.preventDefault();
      e.stopPropagation();
    };

    // Allow scrolling only in elements with overflow-y-auto
    const allowScrollableArea = (e: TouchEvent | WheelEvent) => {
      const target = e.target as HTMLElement;
      const scrollableArea = target.closest('[class*="overflow-y-auto"]');
      if (scrollableArea) {
        // Allow scroll in scrollable area
        return;
      }
      e.preventDefault();
      e.stopPropagation();
    };

    // Prevent focus from causing scroll
    const preventFocusScroll = (e: FocusEvent) => {
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') {
        // Prevent the browser's default scroll behavior
        setTimeout(() => {
          window.scrollTo(0, scrollY);
        }, 0);
      }
    };

    // Add event listeners
    window.addEventListener('scroll', preventScroll, {
      passive: false,
      capture: true,
    });
    window.addEventListener('touchmove', allowScrollableArea, {
      passive: false,
    });
    window.addEventListener('wheel', allowScrollableArea, { passive: false });
    window.addEventListener('focus', preventFocusScroll, { capture: true });

    // Cleanup
    return function cleanup() {
      window.removeEventListener('scroll', preventScroll, { capture: true });
      window.removeEventListener('touchmove', allowScrollableArea);
      window.removeEventListener('wheel', allowScrollableArea);
      window.removeEventListener('focus', preventFocusScroll, {
        capture: true,
      });

      // Restore scrollIntoView
      Element.prototype.scrollIntoView = originalScrollIntoView;

      // Restore scroll position (only body, not html)
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
      window.scrollTo(0, scrollY);
    };
  }, [isPropertyAddRoute]);

  // Track navigation history for back button functionality
  useEffect(() => {
    // Store history stack as JSON array of pathnames
    const historyStack = JSON.parse(
      sessionStorage.getItem('zeme_nav_stack') || '[]'
    ) as string[];

    // Check if this is a back navigation (current path matches a previous entry)
    const existingIndex = historyStack.lastIndexOf(pathname);

    if (existingIndex !== -1 && existingIndex < historyStack.length - 1) {
      // User went back - trim the stack to this point
      historyStack.length = existingIndex + 1;
    } else if (historyStack[historyStack.length - 1] !== pathname) {
      // New navigation - add to stack
      historyStack.push(pathname);
    }

    sessionStorage.setItem('zeme_nav_stack', JSON.stringify(historyStack));
  }, [pathname]);

  return (
    <div
      data-theme={theme}
      className={`h-full ${
        isChatRoute || isAgentMyApplication ? 'overflow-hidden' : ''
      } ${isPropertyAddRoute ? 'max-sm:overflow-hidden max-sm:h-screen' : ''}`}
    >
      {!shouldHideHeader && (
        <Suspense fallback={null}>
          <Header isSticky={isHeaderSticky} />
        </Suspense>
      )}
      <main
        className={`w-full bg-white ${
          isHeaderSticky && !shouldHideHeader && isNavbarVisible
            ? // 89px nav, + the 40px TextZeme bar on explore. Literal classes
              // because Tailwind can't generate arbitrary values from a
              // computed string.
              isExploreRoute
              ? 'sm:mt-[129px] sm:h-[calc(100vh-129px)] h-full'
              : 'sm:mt-[89px] sm:h-[calc(100vh-89px)] h-full'
            : 'h-full '
        } ${isRenterMyApplication ? 'overflow-hidden' : ''} ${
          isAgentMyApplication ? 'overflow-hidden' : ''
        } ${isChatRoute ? 'overflow-hidden' : ''} ${
          isPropertyAddRoute ? 'max-sm:overflow-hidden max-sm:h-screen' : ''
        } ${isCrmRoute ? 'max-sm:h-[100dvh]' : ''}`}
      >
        {children}
        {loaderType === loaderTypes.GENERAL && <Loader isVisible={isLoading} />}
      </main>
      {!shouldHideMobileNav && <MobileNav />}
    </div>
  );
}
